import React, {useState} from "react";
import {Link} from "react-router-dom";
import StatusBadge from "../components/StatusBadge";
import {mediaUrl} from "../components/media";
import {deleteListing, setListingVisibility, submitListing, updateListing} from "../api/listings";

const FILTERS = [
    {id: "all", label: "All", test: () => true},
    {id: "live", label: "Live", test: (l) => l.reviewStatus === "APPROVED" && l.active},
    {id: "review", label: "In review", test: (l) => l.reviewStatus === "IN_REVIEW" || !!l.pendingChanges},
    {id: "todo", label: "Drafts & changes", test: (l) => ["DRAFT", "CHANGES_REQUESTED"].includes(l.reviewStatus)},
    {id: "hidden", label: "Hidden", test: (l) => l.reviewStatus === "APPROVED" && !l.active},
];

/** One row's actions, with an inline confirm for delete (no browser dialogs). */
const ListingActions = ({listing, approved, onChanged, onError}) => {
    const [busy, setBusy] = useState(false);
    const [confirming, setConfirming] = useState(false);
    const run = async (fn) => {
        setBusy(true);
        onError("");
        try {
            await fn();
            await onChanged();
        } catch (err) {
            onError(`${listing.title}: ${err.message}`);
        } finally {
            setBusy(false);
            setConfirming(false);
        }
    };
    const s = listing.reviewStatus;
    if (confirming) {
        return (
            <span className="brk-row-actions">
                <span className="brk-hint">Delete for good?</span>
                <button type="button" className="brk-link brk-link--danger" disabled={busy}
                        onClick={() => run(() => deleteListing(listing.id))}>Yes, delete</button>
                <button type="button" className="brk-link" onClick={() => setConfirming(false)}>Keep</button>
            </span>
        );
    }
    return (
        <span className="brk-row-actions">
            {s !== "REJECTED" && <Link className="brk-link" to={`/broker/listings/${listing.id}`}>Edit</Link>}
            {approved && ["DRAFT", "CHANGES_REQUESTED"].includes(s) && (
                <button type="button" className="brk-link" disabled={busy} onClick={() => run(() => submitListing(listing.id))}>
                    Submit for review
                </button>
            )}
            {approved && s === "APPROVED" && (
                <>
                    <button type="button" className="brk-link" disabled={busy}
                            onClick={() => run(() => setListingVisibility(listing.id, !listing.active))}>
                        {listing.active ? "Hide" : "Show"}
                    </button>
                    <button type="button" className="brk-link" disabled={busy}
                            onClick={() => run(() => updateListing(listing.id, {soldOut: !listing.soldOut}))}>
                        {listing.soldOut ? "Mark available" : "Mark sold out"}
                    </button>
                </>
            )}
            {approved && <button type="button" className="brk-link brk-link--danger" onClick={() => setConfirming(true)}>Delete</button>}
        </span>
    );
};

const ListingsTab = ({listings, approved, onChanged}) => {
    const [filter, setFilter] = useState("all");
    const [error, setError] = useState("");
    const shown = listings.filter(FILTERS.find((f) => f.id === filter).test);

    return (
        <div className="brk-card">
            <div className="brk-card__head">
                <div className="brk-filters" role="group" aria-label="Filter listings">
                    {FILTERS.map((f) => (
                        <button key={f.id} type="button" aria-pressed={filter === f.id}
                                className={`brk-filter${filter === f.id ? " brk-filter--on" : ""}`} onClick={() => setFilter(f.id)}>
                            {f.label} <span>{listings.filter(f.test).length}</span>
                        </button>
                    ))}
                </div>
                {approved && <Link className="brk-btn brk-btn--primary brk-btn--sm" to="/broker/listings/new">+ Add listing</Link>}
            </div>
            {error && <div className="brk-notice brk-notice--error" role="alert">{error}</div>}
            {shown.length === 0 ? (
                <p className="brk-empty">
                    {listings.length === 0 ? "No listings yet. Builders: add your project and its unit types from the Projects tab." : "Nothing here."}
                </p>
            ) : (
                <ul className="brk-items">
                    {shown.map((l) => (
                        <li key={l.id} className="brk-item">
                            <div className="brk-item__thumb">
                                {l.mainImages?.[0] || l.images?.[0]
                                    ? <img src={mediaUrl(l.mainImages?.[0] || l.images?.[0])} alt="" loading="lazy"/>
                                    : <span aria-hidden="true">🏠</span>}
                            </div>
                            <div className="brk-item__body">
                                <div className="brk-item__top">
                                    <Link to={`/broker/listings/${l.id}`} className="brk-item__title">{l.title}</Link>
                                    <StatusBadge status={l.reviewStatus} active={l.active} pendingChanges={l.pendingChanges}/>
                                </div>
                                <p className="brk-item__meta">
                                    {[l.price, l.bhk, l.sqft && `${l.sqft} sqft`, l.location].filter(Boolean).join(" · ")}
                                    {l.soldOut && <span className="brk-badge brk-badge--grey">Sold out</span>}
                                </p>
                                {l.reviewNote && ["CHANGES_REQUESTED", "REJECTED"].includes(l.reviewStatus) && (
                                    <p className="brk-item__note">Reviewer: {l.reviewNote}</p>
                                )}
                                <div className="brk-item__foot">
                                    <span className="brk-hint">{l.leadCount} lead{l.leadCount === 1 ? "" : "s"}</span>
                                    {l.active && <a className="brk-link" href={`/property/${l.slug}`} target="_blank" rel="noreferrer">View on site ↗</a>}
                                    <ListingActions listing={l} approved={approved} onChanged={onChanged} onError={setError}/>
                                </div>
                            </div>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

export default ListingsTab;
